import { useState } from "react";
import { X, Download, Share, PlusSquare, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { isNative } from "@/lib/native";
import { isIOS, isStandalone } from "@/components/InstallButton";

const KEY = "aph_banner_hidden_until";
const WEEK = 7 * 24 * 3600 * 1000;
const isAndroid = () => /android/i.test(navigator.userAgent);
const hidden = () => Number(localStorage.getItem(KEY) || 0) > Date.now();

function IosSteps({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="install-banner-ios-dialog" className="bg-nebula border-rim text-ash sm:max-w-sm rounded-secure w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle className="font-branding">Add Aphelion to your iPhone</DialogTitle>
          <DialogDescription className="text-ash/55">Opens full-screen from its own icon. Works in Safari.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm">
          <li className="flex items-center gap-3"><Share size={20} strokeWidth={1.5} className="text-stellar" /> Tap the <b>Share</b> button in Safari</li>
          <li className="flex items-center gap-3"><PlusSquare size={20} strokeWidth={1.5} className="text-stellar" /> Choose <b>Add to Home Screen</b></li>
        </ol>
      </DialogContent>
    </Dialog>
  );
}

export function InstallBanner() {
  const [show, setShow] = useState(() => !isNative() && !isStandalone() && !hidden());
  const [iosOpen, setIosOpen] = useState(false);
  if (!show) return null;

  const ios = isIOS();
  const android = isAndroid();
  const dismiss = () => { localStorage.setItem(KEY, String(Date.now() + WEEK)); setShow(false); };
  const text = ios ? "Add Aphelion to your Home Screen" : android ? "Get the Aphelion Android app" : "Get Aphelion on your phone";
  const btnCls = "flex items-center gap-1.5 bg-stellar text-void font-branding font-semibold text-xs rounded-secure px-3 py-2 hover:opacity-85 transition-opacity whitespace-nowrap";

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pointer-events-none rise-in" data-testid="install-banner">
      <div className="pointer-events-auto mx-auto max-w-md flex items-center gap-3 bg-nebula/95 backdrop-blur-xl border border-stellar/25 rounded-secure p-3 shadow-stellar-glow">
        <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-secure border border-glass flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-branding font-semibold truncate" data-testid="install-banner-text">{text}</p>
          <p className="text-[11px] text-ash/45 truncate">Zero-knowledge messaging · free</p>
        </div>
        {ios ? (
          <button onClick={() => setIosOpen(true)} data-testid="install-banner-ios-btn" className={btnCls}><PlusSquare size={14} strokeWidth={1.5} /> Add</button>
        ) : android ? (
          <a href="/aphelion.apk" download="Aphelion.apk" data-testid="install-banner-apk-btn" className={btnCls}><Download size={14} strokeWidth={1.5} /> Get app</a>
        ) : (
          <a href="/download" data-testid="install-banner-download-btn" className={btnCls}><Smartphone size={14} strokeWidth={1.5} /> Get app</a>
        )}
        <button onClick={dismiss} data-testid="install-banner-close-btn" aria-label="Dismiss" className="p-1.5 text-ash/45 hover:text-ash"><X size={16} strokeWidth={1.5} /></button>
      </div>
      <IosSteps open={iosOpen} onOpenChange={setIosOpen} />
    </div>
  );
}
